import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Loader2, RefreshCw, Users as UsersIcon,
  Ban, Unlock, Trash2, AlertTriangle, UserCheck,
} from 'lucide-react';
import {
  adminUsersService,
  type AdminUser,
  type FeatureUsage,
  type RegistrationPolicy,
  type UserLoginEvent,
  type UserSummary,
} from '@/services/adminUsersService';
import { useAuth } from '@/auth/AuthContext';
import {
  Pagination, usePagedList, useEmptyPageGuard, useDebounced, useLatestRequest,
} from '@/components/ui/pagination';
import { SearchInput, SortableHeader, EmptyState } from '@/components/ui/list-toolbar';
import { dateOnly, dateTime } from '@/lib/format';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

// ── formatting helpers ──────────────────────────────────────────────

/** Trim a User-Agent to a readable device hint. */
function deviceHint(ua: string | null): string {
  if (!ua) return '—';
  return ua.length > 64 ? `${ua.slice(0, 64)}…` : ua;
}

function OnlineDot({ online }: { online: boolean }) {
  return (
    <span
      title={online ? 'Online' : 'Offline'}
      className={`inline-block h-2 w-2 rounded-full ${
        online ? 'bg-emerald-500' : 'bg-muted-foreground/30'
      }`}
    />
  );
}

function outcomeVariant(outcome: string): 'secondary' | 'destructive' | 'outline' {
  return outcome === 'Success' ? 'secondary' : 'destructive';
}

// ── registration policy card (SuperAdmin) ───────────────────────────

function RegistrationPolicyCard() {
  const [policy, setPolicy] = useState<RegistrationPolicy | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    adminUsersService.getRegistrationPolicy()
      .then((p) => { if (!cancelled) setPolicy(p); })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const toggle = async () => {
    if (!policy) return;
    setBusy(true);
    setError(null);
    try {
      setPolicy(await adminUsersService.setRegistrationEnabled(!policy.registrationEnabled));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update');
    } finally {
      setBusy(false);
    }
  };

  const enabled = policy?.registrationEnabled ?? true;

  return (
    <div className="mb-4 rounded-lg border bg-card p-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-sm font-medium">
            <UserCheck className="h-4 w-4 text-primary" />
            New-account registration
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {loading
              ? 'Loading…'
              : enabled
                ? 'Open — anyone can create an account from the sign-up page.'
                : 'Closed — the sign-up page is disabled and new registrations are rejected.'}
            {policy?.updatedAt && !loading && (
              <> · last changed {dateTime(policy.updatedAt)}</>
            )}
          </p>
          {error && <p className="mt-1 text-xs text-destructive">{error}</p>}
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={enabled ? 'secondary' : 'destructive'}>
            {loading ? '…' : enabled ? 'Enabled' : 'Disabled'}
          </Badge>
          <Button
            size="sm"
            variant={enabled ? 'destructive' : 'default'}
            disabled={loading || busy || !policy}
            onClick={() => void toggle()}
          >
            {busy && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
            {enabled ? 'Disable registration' : 'Enable registration'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── per-user detail dialog ──────────────────────────────────────────

function UserDetailDialog({
  user,
  isSuperAdmin,
  onClose,
  onUserUpdated,
  onUserDeleted,
}: {
  user: AdminUser | null;
  isSuperAdmin: boolean;
  onClose: () => void;
  onUserUpdated: (u: AdminUser) => void;
  onUserDeleted: (userId: string) => void;
}) {
  const [summary, setSummary] = useState<UserSummary | null>(null);
  const [usage, setUsage] = useState<FeatureUsage[]>([]);
  const [logins, setLogins] = useState<UserLoginEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Admin-action state.
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  useEffect(() => {
    // Reset per-action state on every target change (including dialog close).
    // The dialog component stays mounted across opens, so a `busy` flag left
    // over from a previous user's action — e.g. a request that never settled —
    // would otherwise render THIS user's buttons stuck-spinning before any
    // click. Done before the !user guard so it also clears on close.
    setBusy(false);
    setActionError(null);
    setConfirmingDelete(false);
    if (!user) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setSummary(null);
    setUsage([]);
    setLogins([]);
    Promise.all([
      adminUsersService.summary(user.id),
      adminUsersService.usage(user.id),
      adminUsersService.logins(user.id),
    ])
      .then(([s, u, l]) => {
        if (cancelled) return;
        setSummary(s);
        setUsage(u);
        setLogins(l);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load user detail');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  const maxUsage = useMemo(
    () => usage.reduce((m, f) => Math.max(m, f.count), 0),
    [usage],
  );

  const toggleLockout = async () => {
    if (!user) return;
    setBusy(true);
    setActionError(null);
    try {
      onUserUpdated(await adminUsersService.setLockout(user.id, !user.isLockedOut));
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Failed to update account');
    } finally {
      setBusy(false);
    }
  };

  const deleteUser = async (cascade: boolean) => {
    if (!user) return;
    setBusy(true);
    setActionError(null);
    try {
      await adminUsersService.deleteUser(user.id, cascade);
      onUserDeleted(user.id);
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Failed to delete account');
      setBusy(false); // keep dialog open so the error is visible
    }
  };

  return (
    <Dialog open={user !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        {user && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <OnlineDot online={user.online} />
                {user.email}
              </DialogTitle>
            </DialogHeader>

            {/* identity strip */}
            <div className="flex flex-wrap gap-2 text-xs">
              {user.roles.map((r) => (
                <Badge key={r} variant="outline">{r}</Badge>
              ))}
              <Badge variant="secondary">{user.tier}</Badge>
              {user.isLockedOut && <Badge variant="destructive">Disabled</Badge>}
              {user.online && (
                <Badge variant="secondary">
                  {user.connectionCount} connection{user.connectionCount === 1 ? '' : 's'}
                </Badge>
              )}
            </div>

            <div className="mt-2 grid grid-cols-2 gap-x-6 gap-y-1 text-sm sm:grid-cols-3">
              <Field label="Registered" value={dateOnly(user.createdAt)} />
              <Field label="Last login" value={dateTime(user.lastLoginAt)} />
              <Field label="Last login IP" value={user.lastLoginIp ?? '—'} />
              <Field label="Last seen" value={dateTime(user.lastSeenAt)} />
            </div>

            {loading && (
              <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading detail…
              </div>
            )}
            {error && <p className="py-4 text-sm text-destructive">{error}</p>}

            {!loading && !error && (
              <>
                {/* activity summary */}
                <Section title="Activity">
                  <div className="grid grid-cols-3 gap-3">
                    <Stat label="Strategies" value={summary?.strategyCount ?? 0} />
                    <Stat label="Backtest runs" value={summary?.runCount ?? 0} />
                    <Stat label="Scanners" value={summary?.scannerCount ?? 0} />
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">
                    Last activity: {dateTime(summary?.lastActivityAt)}
                  </p>
                </Section>

                {/* per-feature usage */}
                <Section title="Feature usage (last 30 days)">
                  {usage.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No tracked usage.</p>
                  ) : (
                    <div className="space-y-1.5">
                      {usage.map((f) => (
                        <div key={f.feature} className="flex items-center gap-3 text-sm">
                          <span className="w-28 shrink-0">{f.feature}</span>
                          <div className="h-2 flex-1 rounded bg-muted">
                            <div
                              className="h-2 rounded bg-primary"
                              style={{ width: `${maxUsage > 0 ? (f.count / maxUsage) * 100 : 0}%` }}
                            />
                          </div>
                          <span className="w-14 shrink-0 text-right tabular-nums text-muted-foreground">
                            {f.count}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </Section>

                {/* login history */}
                <Section title="Login history">
                  {logins.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No login events.</p>
                  ) : (
                    <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="text-muted-foreground">
                        <tr className="text-left">
                          <th className="py-1 pr-3 font-medium">When</th>
                          <th className="py-1 pr-3 font-medium">Event</th>
                          <th className="py-1 pr-3 font-medium">IP</th>
                          <th className="py-1 font-medium">Device</th>
                        </tr>
                      </thead>
                      <tbody>
                        {logins.map((e, i) => (
                          <tr key={`${e.occurredAt}-${i}`} className="border-t border-border/50">
                            <td className="py-1 pr-3 whitespace-nowrap">{dateTime(e.occurredAt)}</td>
                            <td className="py-1 pr-3">
                              <Badge variant={outcomeVariant(e.outcome)}>
                                {e.kind} · {e.outcome}
                              </Badge>
                            </td>
                            <td className="py-1 pr-3 whitespace-nowrap">{e.ipAddress ?? '—'}</td>
                            <td className="py-1 text-muted-foreground">{deviceHint(e.userAgent)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    </div>
                  )}
                </Section>
              </>
            )}

            {/* admin actions */}
            <Section title="Admin actions">
              {actionError && <p className="mb-2 text-sm text-destructive">{actionError}</p>}
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant={user.isLockedOut ? 'default' : 'outline'}
                  disabled={busy}
                  onClick={() => void toggleLockout()}
                >
                  {busy
                    ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                    : user.isLockedOut
                      ? <Unlock className="mr-1.5 h-3.5 w-3.5" />
                      : <Ban className="mr-1.5 h-3.5 w-3.5" />}
                  {user.isLockedOut ? 'Enable account' : 'Disable account'}
                </Button>
                {isSuperAdmin && !confirmingDelete && (
                  <Button
                    size="sm"
                    variant="destructive"
                    disabled={busy}
                    onClick={() => { setConfirmingDelete(true); setActionError(null); }}
                  >
                    <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                    Delete account…
                  </Button>
                )}
              </div>

              {isSuperAdmin && confirmingDelete && (
                <div className="mt-3 rounded-md border border-destructive/40 bg-destructive/5 p-3">
                  <div className="flex items-center gap-1.5 text-sm font-medium text-destructive">
                    <AlertTriangle className="h-4 w-4" />
                    Delete {user.email}?
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Choose how much to remove. Both options delete the user record and
                    its login history and cannot be undone.
                  </p>
                  <div className="mt-2.5 flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      variant="destructive"
                      disabled={busy}
                      onClick={() => void deleteUser(false)}
                    >
                      {busy && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
                      Delete account only
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      disabled={busy}
                      onClick={() => void deleteUser(true)}
                      title="Also deletes every strategy, run, scanner and setting this user owns"
                    >
                      {busy && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
                      Delete account + all their data
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={busy}
                      onClick={() => setConfirmingDelete(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </Section>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span className="text-muted-foreground">{label}: </span>
      <span className="text-foreground">{value}</span>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mt-4">
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {title}
      </h3>
      {children}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg border bg-card p-3 text-center">
      <div className="text-xl font-semibold tabular-nums">{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  );
}

type UserSortKey = 'email' | 'createdAt' | 'tier' | 'lastLoginAt';

// ── page ────────────────────────────────────────────────────────────

export const UserManagementPage: React.FC = () => {
  const { user: currentUser } = useAuth();
  const isSuperAdmin = currentUser?.roles?.includes('SuperAdmin') ?? false;

  const [users, setUsers] = useState<AdminUser[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [onlineOnly, setOnlineOnly] = useState(false);
  const [selected, setSelected] = useState<AdminUser | null>(null);

  const [sortBy, setSortBy] = useState<UserSortKey>('email');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const debouncedSearch = useDebounced(search, 300);
  const { page, setPage, pageSize, setPageSize } = usePagedList({
    pageSize: 25,
    resetOn: [debouncedSearch, sortBy, sortDir, onlineOnly],
  });

  const beginRequest = useLatestRequest();

  const load = useCallback(async () => {
    const isCurrent = beginRequest();
    setLoading(true);
    setError(null);
    try {
      if (onlineOnly) {
        // Presence lives in memory, not in the database, so the online view cannot be
        // server-paged against it. That list is small by nature, so it is fetched whole
        // and filtered here — and the count below reflects exactly what is shown.
        const online = await adminUsersService.onlineList();
        const q = debouncedSearch.trim().toLowerCase();
        const matched = q ? online.filter((u) => u.email.toLowerCase().includes(q)) : online;
        if (!isCurrent()) return;
        setUsers(matched);
        setTotal(matched.length);
      } else {
        const result = await adminUsersService.listPaged({
          page, pageSize, sortBy, sortDir, search: debouncedSearch || undefined,
        });
        // Discard a superseded page - see useLatestRequest. Toggling "online only"
        // swaps between two different endpoints, so this also stops the slower of the
        // two from landing after the user has switched away from it.
        if (!isCurrent()) return;
        setUsers(result.items);
        setTotal(result.total);
      }
    } catch (e) {
      if (!isCurrent()) return;
      setError(e instanceof Error ? e.message : 'Failed to load users');
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [onlineOnly, page, pageSize, sortBy, sortDir, debouncedSearch, beginRequest]);

  useEffect(() => { void load(); }, [load]);

  // Deleting the last user on a page must not strand the view on an empty one.
  useEmptyPageGuard(page, setPage, users.length, total);

  // Replace one user in the list (and the open dialog) after a lockout change.
  const handleUserUpdated = (u: AdminUser) => {
    setUsers((prev) => prev.map((x) => (x.id === u.id ? u : x)));
    setSelected((cur) => (cur && cur.id === u.id ? u : cur));
  };

  // Re-query rather than splicing the row out locally: the list is server-paged, so a
  // local removal would leave `total` overstating the count and could strand the user
  // on a page that is now empty.
  const handleUserDeleted = (_userId: string) => {
    setSelected(null);
    void load();
  };

  // Online count is of the CURRENT PAGE unless the online view is active, where it
  // is the whole set. Labelled accordingly below rather than passed off as a total.
  const onlineOnPage = useMemo(() => users.filter((u) => u.online).length, [users]);

  const toggleSort = (column: UserSortKey) => {
    if (sortBy === column) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortBy(column); setSortDir('asc'); }
  };

  const pager = (compact: boolean) => (
    <Pagination
      page={page}
      pageSize={pageSize}
      total={total}
      onPageChange={setPage}
      onPageSizeChange={compact ? undefined : setPageSize}
      compact={compact}
      loading={loading}
      itemLabel="users"
    />
  );

  return (
    <div className="h-full overflow-y-auto p-4">
      {/* header */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <UsersIcon className="h-5 w-5 text-primary" />
          <h1 className="text-lg font-semibold">User Management</h1>
          <span className="text-sm text-muted-foreground">
            {total.toLocaleString()} user{total === 1 ? '' : 's'}
            {onlineOnly ? ' online' : ` · ${onlineOnPage} online on this page`}
          </span>
        </div>
        <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
          <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* registration policy — SuperAdmin only */}
      {isSuperAdmin && <RegistrationPolicyCard />}

      {/* filters */}
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search by email…"
          className="w-64"
        />
        <label className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <input
            type="checkbox"
            checked={onlineOnly}
            onChange={(e) => setOnlineOnly(e.target.checked)}
          />
          Online only
        </label>
        {!onlineOnly && total > pageSize && <div className="ml-auto">{pager(true)}</div>}
      </div>

      {/* states */}
      {loading && (
        <div className="flex items-center gap-2 py-10 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading users…
        </div>
      )}
      {error && <p className="py-6 text-sm text-destructive">{error}</p>}

      {/* table */}
      {!loading && !error && (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-muted-foreground">
              <tr className="text-left">
                <th className="w-8 py-2 pl-3" />
                <th className="py-2 pr-3 font-medium">
                  <SortableHeader column="email" label="Email" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                </th>
                <th className="py-2 pr-3 font-medium">Roles</th>
                <th className="py-2 pr-3 font-medium">
                  <SortableHeader column="tier" label="Tier" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                </th>
                <th className="py-2 pr-3 font-medium">
                  <SortableHeader column="createdAt" label="Registered" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                </th>
                <th className="py-2 pr-3 font-medium">
                  <SortableHeader column="lastLoginAt" label="Last login" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                </th>
                <th className="py-2 pr-3 font-medium">IP</th>
                <th className="py-2 pr-3 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr
                  key={u.id}
                  onClick={() => setSelected(u)}
                  className="cursor-pointer border-t hover:bg-muted/50"
                >
                  <td className="py-2 pl-3"><OnlineDot online={u.online} /></td>
                  <td className="py-2 pr-3 font-medium">{u.email}</td>
                  <td className="py-2 pr-3">
                    <div className="flex flex-wrap gap-1">
                      {u.roles.map((r) => (
                        <Badge key={r} variant="outline">{r}</Badge>
                      ))}
                    </div>
                  </td>
                  <td className="py-2 pr-3 text-muted-foreground">{u.tier}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{dateOnly(u.createdAt)}</td>
                  <td className="py-2 pr-3 text-muted-foreground whitespace-nowrap">
                    {dateTime(u.lastLoginAt)}
                  </td>
                  <td className="py-2 pr-3 text-muted-foreground">{u.lastLoginIp ?? '—'}</td>
                  <td className="py-2 pr-3">
                    {u.isLockedOut
                      ? <Badge variant="destructive">Disabled</Badge>
                      : <span className="text-muted-foreground">—</span>}
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr>
                  <td colSpan={8} className="p-0">
                    <EmptyState
                      title={search ? 'No users match that search' : 'No users to show'}
                      hint={onlineOnly ? 'Nobody has a live connection right now.' : undefined}
                    />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          {total > 0 && !onlineOnly && <div className="border-t p-3">{pager(false)}</div>}
        </div>
      )}

      <UserDetailDialog
        user={selected}
        isSuperAdmin={isSuperAdmin}
        onClose={() => setSelected(null)}
        onUserUpdated={handleUserUpdated}
        onUserDeleted={handleUserDeleted}
      />
    </div>
  );
};

export default UserManagementPage;
